import React, { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

// Material UI Core
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import CardContent from "@mui/material/CardContent";

// UI
import GridContainer from "@/ui/Grid/GridContainer";
import GridItem from "@/ui/Grid/GridItem";
import { StackedChart } from "@/ui/Charts/StackedChart";
import { DonutChart } from "@/ui/Charts/DonutChart";
import { PieChart } from "@/ui/Charts/PieChart";

// Services
import statsService from "@/services/statsService";

// Context
import OperationsContext from "@/components/OperationsContext/OperationsContext";

// Types
import { NonAxisChartData } from "@/@types/ui/Charts";

function Dashboard() {
  const { t } = useTranslation();

  const operationContext = useContext(OperationsContext);

  const [sifreFishneta, setSifreFishneta] = useState<string[]>([]);

  // Objekti u projektu
  const [objektiUProjektuData, setObjektiUProjektuData] =
    useState<NonAxisChartData>({
      series: [],
      labels: [],
    });

  // Objekti po fishnetima
  const [objektiPoFishnetimaData, setObjektiPoFishnetimaData] =
    useState<ApexAxisChartSeries>([]);

  // Objekti po fishnetu
  const [objektiUFishnetuData, setObjektiUFishnetuData] =
    useState<NonAxisChartData>({
      series: [],
      labels: [],
    });

  // Lokacije obveznika
  const [lokacijeAktivnihObveznikaData, setLokacijeAktivnihObveznikaData] =
    useState<NonAxisChartData>({
      series: [],
      labels: [],
    });

  // Objekti prema namjena bez aktivnih obveznika
  const [objektiNamjenaBezObveznikaData, setObjektiNamjenaBezObveznikaData] =
    useState<ApexAxisChartSeries>([]);

  // Objekti bez lokacije
  const [objektiBezLokacijaData, setObjektiBezLokacijaData] =
    useState<ApexAxisChartSeries>([]);

  // Usporedbe adresa obveznika i objekta
  const [adresaData, setAdresaData] = useState<NonAxisChartData>({
    series: [],
    labels: [],
  });

  // Pregledano operatera po datumima
  const [operateriData, setOperateriData] = useState<ApexAxisChartSeries>([]);

  // Namjena objekata
  const [namjenaData, setNamjenaData] = useState<NonAxisChartData>({
    series: [],
    labels: [],
  });

  const pastSevenDays = useMemo(() => {
    const dates: string[] = [];

    for (let i = 0; i < 7; i++) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const formatted = date.toISOString().split("T")[0];
      dates.push(formatted);
    }

    return dates.reverse();
  }, []);

  const fetchStatsProject = () => {
    statsService
      .getProjectStats(Number(operationContext.selectedProject?.id))
      .then((result) => {
        if (result.success) {
          const { cnt_pregledano, cnt_nepregledano } = result.data;
          setObjektiUProjektuData({
            series: [Number(cnt_pregledano), Number(cnt_nepregledano)],
            labels: [
              t("dashboard.looked_through"),
              t("dashboard.not_looked_through"),
            ],
          });
          setLokacijeAktivnihObveznikaData({
            series: [
              Number(result.data.obveznici_lokacije.obveznici_bez_lokacije),
              Number(result.data.obveznici_lokacije.obveznici_s_lokacijom),
            ],
            labels: [
              t("dashboard.obligee_without_location"),
              t("dashboard.obligee_with_location"),
            ],
          });

          if (
            Array.isArray(result.data.fishnets) &&
            result.data.fishnets.length > 0
          ) {
            const pregFPP = [];
            const nePregFPP = [];
            const fishnetSifre = [];
            const stambenaNamjenaNew = [];
            const javnaNamjenaNew = [];
            const poslovnaNamjenaNew = [];
            const garaznaNamjenaNew = [];
            const uFunkcijiObjektaNew = [];
            const objektiBezLokacije = [];

            for (let i = 0; i < result.data?.fishnets?.length || 0; i++) {
              pregFPP.push(Number(result.data.fishnets[i].cnt_pregledano));
              nePregFPP.push(Number(result.data.fishnets[i].cnt_nepregledano));
              if (result.data.objekti_bez_obveznika) {
                var element = result.data.objekti_bez_obveznika.find(
                  (objekt: { sifra: number }) =>
                    objekt.sifra === result.data.fishnets[i].sifra
                );
                 if (element) {
                  stambenaNamjenaNew.push(Number(element.stambena));
                  javnaNamjenaNew.push(Number(element.javna));
                  poslovnaNamjenaNew.push(Number(element.poslovna));
                  garaznaNamjenaNew.push(Number(element.garazna));
                  uFunkcijiObjektaNew.push(Number(element.u_funkciji_objekta));
                } else {
                  stambenaNamjenaNew.push(0);
                  javnaNamjenaNew.push(0);
                  poslovnaNamjenaNew.push(0);
                  garaznaNamjenaNew.push(0);
                  uFunkcijiObjektaNew.push(0);
                }
              }
              if (result.data.objekti_bez_lokacije)
                var element = result.data.objekti_bez_lokacije.find(
                  (objekt: { sifra: number }) =>
                    objekt.sifra === result.data.fishnets[i].sifra
                );
              if (element)
                objektiBezLokacije.push(element.objekti_bez_lokacije);
              else objektiBezLokacije.push(0);
              fishnetSifre.push(
                Number(result.data.fishnets[i].sifra) + ". fishnet"
              );
            }
            setObjektiPoFishnetimaData([
              {
                name: t("dashboard.num_looked_trough"),
                data: pregFPP,
              },
              {
                name: t("dashboard.num_not_looked_trough"),
                data: nePregFPP,
              },
            ]);
            setSifreFishneta(fishnetSifre);
            setObjektiNamjenaBezObveznikaData([
              {
                name: t("dashboard.residential"),
                data: stambenaNamjenaNew,
              },
              {
                name: t("dashboard.public"),
                data: javnaNamjenaNew,
              },
              {
                name: t("dashboard.bussiness"),
                data: poslovnaNamjenaNew,
              },
              {
                name: t("dashboard.garage"),
                data: garaznaNamjenaNew,
              },
              {
                name: t("dashboard.object"),
                data: uFunkcijiObjektaNew,
              },
            ]);
            setObjektiBezLokacijaData([
              {
                name: t("dashboard.objects_without_location"),
                data: objektiBezLokacije,
              },
            ]);
          }

          if (result.data.namjena) {
            const namjena_sums: any[] = [];
            const namjena_labeles: string[] = [];

            result.data.namjena.forEach((n: any) => {
              namjena_sums.push(n.sum);
              namjena_labeles.push(n.naziv || "bez namjene");
            });

            const namjena_obj = {
              series: namjena_sums,
              labels: namjena_labeles,
            };

            setNamjenaData(namjena_obj);
          }

          if (result.data.razlicite_adrese) {
            setAdresaData({
              series: [
                result.data.razlicite_adrese.iste_adrese,
                result.data.razlicite_adrese.razlicite_adrese,
              ],
              labels: [
                t("dashboard.same_adress"),
                t("dashboard.different_adress"),
              ],
            });
          }
        }

        if (result.data.operatori) {
          const { operatori } = result.data;
          const operatoriArr: string[] = operatori
            // @ts-ignore
            .map((x) => x.username) // extract only the usernames
            .filter(
              // remove duplicates
              // @ts-ignore
              (x, index, self) => index === self.findIndex((y) => y === x)
            )
            .sort();

          const data = operatoriArr.map((op) => {
            return {
              name: op,
              data: pastSevenDays.map(
                (day) =>
                  operatori.find(
                    // @ts-ignore
                    (x) => x.pregledano_on === day && x.username === op
                  )?.cnt_pregledano || 0
              ),
            };
          });

          setOperateriData(data);
        }
      });
  };

  const fetchStatsFishnet = () => {
    statsService
      .getFishnetStats(
        Number(operationContext.selectedProject?.id),
        Number(operationContext.selectedFishnet?.id)
      )
      .then((result) => {
        if (result.success) {
          setObjektiUFishnetuData({
            series: [
              Number(result.data.cnt_pregledano),
              Number(result.data.cnt_nepregledano),
            ],
            labels: [
              t("dashboard.looked_through"),
              t("dashboard.not_looked_through"),
            ],
          });
        }
      });
  };
  useEffect(() => {
    setObjektiUFishnetuData({ series: [], labels: [] });
    if (operationContext.selectedProject?.id) {
      fetchStatsProject();
    }
    if (operationContext.selectedProject?.id === undefined) {
      setObjektiUProjektuData({ series: [], labels: [] });
      setObjektiPoFishnetimaData([]);
      setSifreFishneta([]);
      setLokacijeAktivnihObveznikaData({ series: [], labels: [] });
      setObjektiNamjenaBezObveznikaData([]);
      setObjektiBezLokacijaData([]);
      setNamjenaData({ series: [], labels: [] });
      setAdresaData({ series: [], labels: [] });
    }
  }, [operationContext.selectedProject?.id]);

  useEffect(() => {
    if (operationContext.selectedProject?.id !== 0) {
      if (operationContext.selectedFishnet?.id) {
        fetchStatsFishnet();
      }
    }
    if (operationContext.selectedFishnet?.id === undefined) {
      setObjektiUFishnetuData({ series: [], labels: [] });
    }
  }, [operationContext.selectedFishnet?.id]);

  return (
    <GridContainer>
      <GridItem xs={4}>
        <Card style={{ height: "100%" }}>
          <CardHeader
            title={t("dashboard.objects_in_project")}
            style={{ textAlign: "center" }}
          />
          <CardContent>
            <DonutChart data={objektiUProjektuData} />
          </CardContent>
        </Card>
      </GridItem>
      <GridItem xs={8}>
        <Card style={{ height: "100%" }}>
          <CardHeader
            title={t("dashboard.objects_by_fishnets")}
            style={{ textAlign: "center" }}
          />
          <CardContent>
            <StackedChart
              data={objektiPoFishnetimaData}
              categories={sifreFishneta}
            />
          </CardContent>
        </Card>
      </GridItem>
      <GridItem xs={6}>
        <Card style={{ height: "100%" }}>
          <CardHeader
            title={t("dashboard.objects_in_fishnet")}
            style={{ textAlign: "center" }}
          />
          <CardContent>
            <DonutChart data={objektiUFishnetuData} />
          </CardContent>
        </Card>
      </GridItem>
      <GridItem xs={6}>
        <Card style={{ height: "100%" }}>
          <CardHeader
            title={t("dashboard.objects_purpose")}
            style={{ textAlign: "center" }}
          />
          <CardContent>
            <PieChart data={namjenaData} />
          </CardContent>
        </Card>
      </GridItem>
      <GridItem xs={4}>
        <Card style={{ height: "100%" }}>
          <CardHeader
            title={t("dashboard.active_obligee_location")}
            style={{ textAlign: "center" }}
          />
          <CardContent>
            <DonutChart data={lokacijeAktivnihObveznikaData} />
          </CardContent>
        </Card>
      </GridItem>
      <GridItem xs={8}>
        <Card style={{ height: "100%" }}>
          <CardHeader
            title={t("dashboard.objects_without_obligee")}
            style={{ textAlign: "center" }}
          />
          <CardContent>
            <StackedChart
              data={objektiNamjenaBezObveznikaData}
              categories={sifreFishneta}
            />
          </CardContent>
        </Card>
      </GridItem>
      <GridItem xs={7}>
        <Card style={{ height: "100%" }}>
          <CardHeader
            title={t("dashboard.objects_without_location")}
            style={{ textAlign: "center" }}
          />
          <CardContent>
            <StackedChart
              data={objektiBezLokacijaData}
              categories={sifreFishneta}
            />
          </CardContent>
        </Card>
      </GridItem>
      <GridItem xs={5}>
        <Card style={{ height: "100%" }}>
          <CardHeader
            title={t("dashboard.obligee_adress")}
            style={{ textAlign: "center" }}
          />
          <CardContent>
            <PieChart data={adresaData} />
          </CardContent>
        </Card>
      </GridItem>
      <GridItem xs={12}>
        <Card style={{ height: "100%" }}>
          <CardHeader
            title={t("dashboard.objects_per_day")}
            style={{ textAlign: "center" }}
          />
          <CardContent>
            <StackedChart data={operateriData} categories={pastSevenDays} />
          </CardContent>
        </Card>
      </GridItem>
    </GridContainer>
  );
}
export default Dashboard;
